import { useRef } from "react";
import { Box, Button, Grid2 as Grid, Paper, Stack, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification } from "@refinedev/core";

import { Form, Modal, ModalRef } from "@shared/components";
import { AgreementsAutocomplete } from "@shared/components/AutocompleteFields";
import { useGenerateFolderMutation } from "@modules/folderGenerator/services/mutations";
import {
  folderGeneratorSchema,
  FolderGeneratorFormValues,
} from "@modules/folderGenerator/validations";

export function FolderGenerator() {
  const { open: openNotification } = useNotification();
  const successModalRef = useRef<ModalRef>(null);

  const methods = useForm<FolderGeneratorFormValues>({
    resolver: zodResolver(folderGeneratorSchema()),
    defaultValues: {
      image: undefined,
      agreement_ids: [],
    },
  });

  const { mutate, isPending } = useGenerateFolderMutation({
    onSuccess: () => {
      methods.reset({ image: undefined, agreement_ids: [] });
      successModalRef.current?.open();
    },
    onError: (error: any) => {
      openNotification?.({
        type: "error",
        message:
          error?.response?.data?.error ||
          "Erro ao iniciar a geração do folder.",
      });
    },
  });

  const handleSubmit = methods.handleSubmit((data) => {
    mutate(data);
  });

  return (
    <Grid container width={1} spacing={2}>
      <Paper sx={{ padding: "24px", width: "100%" }}>
        <Stack direction="column" spacing={3}>
          <Typography variant="h5" gutterBottom>
            Gerador de Folder
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Envie a imagem base do folder e selecione os convênios cujas
            logos devem ser inseridas. Se nenhum convênio for selecionado, o
            folder será gerado para todos os convênios.
          </Typography>

          <FormProvider {...methods}>
            <Stack direction="column" spacing={3}>
              <Stack direction="column" spacing={1.5}>
                <Typography variant="subtitle1" fontWeight="bold">
                  1. Convênios
                </Typography>
                <AgreementsAutocomplete
                  name="agreement_ids"
                  label="Convênios (opcional — vazio = todos)"
                  multiple
                  queryHookFilters={{ only_with_products: true }}
                />
              </Stack>

              <Stack direction="column" spacing={1.5}>
                <Typography variant="subtitle1" fontWeight="bold">
                  2. Imagem do folder
                </Typography>
                <Form.SinglePhotoPicker
                  name="image"
                  shape="square"
                  size={150}
                />
              </Stack>

              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isPending}
                  size="large"
                >
                  {isPending ? "Gerando..." : "Gerar Folder"}
                </Button>
              </Box>
            </Stack>
          </FormProvider>
        </Stack>
      </Paper>

      <Modal
        ref={successModalRef}
        title="Folder em geração"
        hideConfirmButton
        cancelLabel="Fechar"
      >
        <Typography variant="body1">
          A geração do folder foi iniciada. Esse processo pode levar minutos
          ou horas, dependendo da quantidade de convênios. Você receberá o
          arquivo .zip por e-mail assim que estiver pronto.
        </Typography>
      </Modal>
    </Grid>
  );
}
